(function () {
    /**
     * Plug Fraction.
     * @typedef {Object} Fraction
     * @property {number} numerator - integer
     * @property {number} denominator - integer
     * @property {() => string} toString
     * @property {() => string} toFixed
     * @property {() => number} toDecimal
     * @property {() => Fraction} simplify
     * @property {(value: number | Fraction) => Fraction} plus
     * @property {(value: number | Fraction) => Fraction} minus
     * @property {(value: number | Fraction) => Fraction} divideBy
     * @property {(value: number | Fraction) => Fraction} times
     * @property {() => Fraction} fround
     * @property {(value: (number | Fraction)) => boolean} equals
     * @property {(value: (number | Fraction)) => boolean} isGreaterThan
     * @property {(value: (number | Fraction)) => boolean} isLessThan
     * @property {(value: (number | Fraction)) => boolean} isLessThanOrEqualTo
     * @property {(value: (number | Fraction)) => boolean} isGreaterThanOrEqualTo
     */

    const invalidCharsRegex = /[^0-9-+]/;
    const oneHyphenRegex = /^(-\d+|\d+-\d+)$/;  // e.g. -x or x-y
    const twoHyphenRegex = /^(-\d+-\d+)$/;  // e.g. -x-y
    const threeHyphenRegex = /^(-\d+--\d+)$/;  // e.g. -x--y

    // Fraction regexes
    const fracInvalidCharsRegex = /[^0-9/+-]/;
    const fracOneHyphenRegex = /^(-\d+\/\d+|\d+\/\d+-\d+\/\d+|\d+\/\d+-\d+|\d+-\d+\/\d+)$/; // -a/b | a/b-b/c | a/b-c | a-b/c
    const fracTwoHyphenRegex =/^(-\d+\/\d+-\d+\/\d+|-\d+\/\d+-\d+|-\d+-\d+\/\d+)$/; // -a/b-c/d | -a/b-c | -a-b/c
    const fracThreeHyphenRegex = /^(-\d+\/\d+--\d+\/\d+|-\d+\/\d+--\d+|-\d+--\d+\/\d+)$/; // -a/b--c/d | -a/b--c | -a--c/d

    /**
     * Compute the least common multiple of two integers.
     *
     * @param {number} a - integer
     * @param {number} b - integer
     */
    const computeLcm = function (a, b) {
        if (a === b) {
            return a;
        }
        const smaller = (a < b) ? a : b;
        const bigger = (a > b) ? a : b;
        let k = 2;
        while (k < smaller * bigger) {
            const product = smaller * k;
            const isMultipleOfBigger = (product / bigger) % 1 === 0;
            if (isMultipleOfBigger) {
                return product;
            }
            k += 1;
        }
        return smaller * bigger;
    };

    /**
     * Compute the greatest common factor of two integers.
     *
     * @param {number} a - integer
     * @param {number} b - integer
     */
    const computeGcf = function (a, b) {
        if (a === b) {
            return a;
        }
        const smaller = (a < b) ? a : b;
        const bigger = (a > b) ? a : b;
        let k = smaller;
        while (k > 1) {
            if (
                smaller % k === 0
                && bigger % k === 0
            ) {
                return k;
            }
            k -= 1;
        }
        return 1;
    };

    /**
     * Returns the greater of two fractions.
     *
     * @param {Fraction} a
     * @param {Fraction} b
     */
    const maxFraction = function (a, b) {
        if (a.isGreaterThan(b)) {
            return a;
        }
        return b;
    };

    /**
     * Returns the lesser of two fractions.
     *
     * @param {Fraction} a
     * @param {Fraction} b
     */
    const minFraction = function (a, b) {
        if (a.isLessThan(b)) {
            return a;
        }
        return b;
    };

    /**
     * @param {string | [number, number]} value - n/n or n
     * @return {Fraction}
     */
    const fraction = function (value) {
        let numerator;
        let denominator;
        if (typeof value === "string") {
            const parts = value.split("/");
            numerator = Number(parts[0]);
            denominator = parts.length > 1 ? Number(parts[1]) : 1;
        } else {
            numerator = value[0];
            denominator = value[1];
        }

        if (numerator === 0) {
            denominator = 1;
        }

        if (denominator === 0) {
            throw "Cannot divide by 0.";
        }

        /**
         * @return {string}
         */
        const toString = () => {
            return (
                denominator === 1
                ? String(numerator)
                : `${String(numerator)}/${String(denominator)}`
            );
        };

        const toFixed = () => {
            let numStr = numerator.toFixed(2);
            let denomStr = denominator.toFixed(2);
            if (numStr[numStr.length - 2] === "0") {
                if (numStr[numStr.length - 1] === "0") {
                    numStr = numStr.split(".")[0];
                } else {
                    numStr = numStr.slice(0, numStr.length - 1);
                }
            }
            if (denomStr[denomStr.length - 2] === "0") {
                if (denomStr[denomStr.length - 1] === "0") {
                    denomStr = denomStr.split(".")[0];
                } else {
                    denomStr = denomStr.slice(0, denomStr.length - 1);
                }
            }
            return `${numStr}/${denomStr}`;
        };

        /**
         * @return {number}
         */
        const toDecimal = () => {
            return numerator / denominator;
        };

        /**
         * @return {Fraction}
         */
        const simplify = () => {
            if (numerator === 0) {
                return fraction([0, 1]);
            }
            const gcf = computeGcf(numerator, denominator);
            return fraction([numerator / gcf, denominator / gcf]);
        };

        /**
         * @param {number | Fraction} value
         * @return {Fraction}
         */
        const plus = (value) => {
            if (typeof value === "number") {
                value = fraction([value, 1]);
            }
            if (value.denominator === denominator) {
                return fraction([numerator + value.numerator, denominator]);
            }
            const lcm = computeLcm(denominator, value.denominator);
            return fraction([
                ((lcm / denominator) * numerator) + ((lcm / value.denominator) * value.numerator),
                lcm
            ]).simplify();
        };

        /**
         * @param {number | Fraction} value
         * @return {Fraction}
         */
        const minus = (value) => {
            return (
                typeof value === "number"
                ? plus(-1 * value)
                : plus(value.times(-1))
            );
        };

        /**
         * @param {number | Fraction} value
         * @return {Fraction}
         */
        const times = (value) => {
            if (typeof value === "number") {
                return fraction([numerator * value, denominator]);
            } else {
                return fraction([numerator * value.numerator, denominator * value.denominator]);
            }
        };

        /**
         * @param {number | Fraction} value
         * @return {Fraction}
         */
        const divideBy = (value) => {
            if (typeof value === "number") {
                return times(fraction([1, value]));
            } else {
                return times(fraction([value.denominator, value.numerator]));
            }
        };

        const fround = () => {
            return fraction([Math.fround(numerator), Math.fround(denominator)]);
        };

        /**
         * @param {number | Fraction} value
         * @return {boolean}
         */
        const equals = (value) => {
            const decimalValue = (typeof value === "number" ? value : value.toDecimal());
            return (Math.abs(toDecimal() - decimalValue) < Number.EPSILON);
        };

        /**
         * @param {number | Fraction} value
         * @return {boolean}
         */
        const isLessThan = (value) => {
            const decimalValue = (typeof value === "number" ? value : value.toDecimal());
            return (decimalValue - toDecimal() > Number.EPSILON);
        };

        /**
         * @param {number | Fraction} value
         * @return {boolean}
         */
        const isGreaterThan = (value) => {
            const decimalValue = (typeof value === "number" ? value : value.toDecimal());
            return (toDecimal() - decimalValue > Number.EPSILON);
        };

        /**
         * @param {number | Fraction} value
         * @return {boolean}
         */
        const isLessThanOrEqualTo = (value) => {
            return isLessThan(value) || equals(value);
        };

        /**
         * @param {number | Fraction} value
         * @return {boolean}
         */
        const isGreaterThanOrEqualTo = (value) => {
            return isGreaterThan(value) || equals(value);
        };

        return Object.freeze({
            numerator: numerator,
            denominator: denominator,
            toString,
            toFixed,
            toDecimal,
            simplify,

            plus,
            minus,
            times,
            divideBy,
            fround,

            equals,
            isLessThan,
            isGreaterThan,
            isLessThanOrEqualTo,
            isGreaterThanOrEqualTo,
        });
    };

    /**
     * Takes a "FilterValue.value" string and determines if it is formatted correctly for use
     * with the PlugRangeSlider component. Valid formats are those where the string can be
     * converted to a number or a range of numbers, e.g. x or [x, y].
     *
     * @param {string} value
     * @return {boolean}
     */
    const valueHasValidFormat = function (value) {
        if (!value.length) {
            return false;
        }

        // If the value contains a character other than numbers, "-", or "+", it is invalid.
        if (fracInvalidCharsRegex.test(value)) {
            return false;
        }

        // If the value contains a "+" and it is not the last character, it is invalid.
        if (
            value.includes("+")
            && value.indexOf("+") !== value.length - 1
        ) {
            return false;
        }

        value = value.replace("+", "");

        // If hyphens are used, there are only three valid formats: -x, -x-y, -x--y.
        if (value.includes("-")) {
            const chars = value.split("");
            const numHyphens = chars.filter(char => char === "-").length;

            if (numHyphens === 1) {
                if (!fracOneHyphenRegex.test(value) && !oneHyphenRegex.test(value)) {
                    return false;
                }
            } else if (numHyphens === 2) {
                if (!fracTwoHyphenRegex.test(value) && !twoHyphenRegex.test(value)) {
                    return false;
                }
            } else if (numHyphens === 3) {
                if (!fracThreeHyphenRegex.test(value) && !threeHyphenRegex.test(value)) {
                    return false;
                }
            } else {
                return false;
            }
        }

        return true;
    };

    /**
     * Takes a "FilterValue.value" string and, if possible, returns the numeric value. Otherwise,
     * returns undefined. Examples of valid inputs would be: "1", "1+", "1-5", "-1-5", "-8--1".
     * These would be converted to: 1, 1, [1, 5], [-1, 5], and [-8, -1], respectively.
     *
     * @param {string} value
     * @return {Fraction | [Fraction, Fraction] | undefined}
     */
    const convertValueToNumberOrRange = function (value) {
        if (!valueHasValidFormat(value)) {
            return undefined;
        }

        // Strip spaces.
        value = value.replace(/\s/g, "");

        // Count hyphens.
        const chars = value.split("");
        const numHyphens = chars.filter(char => char === "-").length;

        let interval;

        if (numHyphens === 0) {
            return fraction(value.replace("+", ""));
        } else if (numHyphens === 1) {
            if (value[0] === "-") {
                return fraction(value);
            } else {
                interval = value.split("-").map(str => fraction(str));
            }
        } else if (numHyphens === 2) {
            const parts = value.slice(1).split("-").map(str => fraction(str));
            interval = [parts[0].times(-1), parts[1]];
        } else {
            // Assert: numHyphens === 3
            const parts = value.split("--").map(str => fraction(str));
            interval = [parts[0].times(-1), parts[1].times(-1)];
        }
        // Make sure that first value in interval is less than second value.
        if (interval[0].toDecimal() < interval.toDecimal()) {
            return interval;
        } else if (interval[0].equals(interval[1])) {
            return interval[0];
        } else {
            return undefined;
        }
    };

    class PlugFractionRangeSlider extends PlugRangeSlider {
        constructor() {
            super();
        }

        init() {
            this.relevantFilterValues = this.filterData.values.filter(value => value.count || value.active);
            /** @type {Fraction | [Fraction, Fraction] | undefined} */
            this.numericalValues = this.relevantFilterValues.map(value => convertValueToNumberOrRange(
                this.units === "Inches" ? this.preProcessInchesValue(value.value)
                : this.units === "Feet" ? this.preProcessFeetValue(value.value)
                : value.value
            ));

            if (!this.numericalValues.some(value => value !== undefined)) {
                this.hideParentBlock();
                return;
            }
            this.showParentBlock();

            const [searchParamStartValue, searchParamEndValue] = this.getStateFromSearchParams();

            const [minValue, maxValue] = this.computeMinAndMaxValues(this.numericalValues);
            this.minValue = minValue;
            this.maxValue = maxValue;
            this.totalRange = this.maxValue.minus(this.minValue);

            // The idea here is that if someone adjusts the slider, then re-adjusts it before the page
            // has rendered from the first adjustment, we want to cancel that first page render (keeping
            // in mind that this component will be removed during the render process). The caveat here is
            // that we don't want to delay the page fetch/update process, otherwise we would use a standard
            // debounce approach.
            //
            // Each time the emitChange method is called, we insert a new quitEarly object into the
            // following array and pass the object to the "PLUG.collectionFilters.handleChange" function.
            // When/if emitChange is called again, we set the last quitEarly object to true and add a new
            // one to the array.
            //
            // The "handleChange" function triggers a fetch request (for the sections) followed by the
            // page render process. After the fetch request, but before the page render, it checks the
            // value of the quitEarly object it received. If quitEarly is true, it quits early without
            // rendering anything.

            /** @type {{quitEarly: boolean}[]} */
            this.quitEarlyObjects = [];

            this.setLineLabelValues();

            this.setState(
                searchParamStartValue ? maxFraction(this.minValue, fraction(searchParamStartValue)) : this.minValue,
                searchParamEndValue ? minFraction(this.maxValue, fraction(searchParamEndValue)) : this.maxValue
            );

            if (searchParamStartValue !== null || searchParamEndValue !== null) {
                // Determine if current selection is empty (has no corresponding products).
                const currentRange = [this.currentStartValue, this.currentEndValue];

                // Determine if currentRange overlaps one or more numerical values.
                const hasAtLeastOneResult = this.numericalValues.find(value => {
                    return this.valueIsInRange(value, currentRange[0], currentRange[1]);
                });

                if (hasAtLeastOneResult === undefined) {
                    this.currentRangeHasNoResults = true;
                }
            }

            if (
                !this.currentStartValue.equals(this.minValue)
                || !this.currentEndValue.equals(this.maxValue)
            ) {
                this.updateFormInputs(this.currentStartValue, this.currentEndValue);
            }
        }

        /** @param {HTMLAnchorElement} activeFilterLink */
        reformatActiveFilter(activeFilterLink) {
            if (!this.isInitialized) {
                this.preInitLinkToReformat = activeFilterLink;
                return;
            }

            // compute href
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete(this.dataset.paramName);
            searchParams.delete(`${this.dataset.metaobjectId}-start`);
            searchParams.delete(`${this.dataset.metaobjectId}-end`);
            activeFilterLink.href = `${location.pathname}?${searchParams.toString()}`;

            // compute text content.
            const rangeSpan = activeFilterLink.querySelector(".plug-active-filter-range");
            const startValue = this.roundFractionToNearestSixteenth(this.currentStartValue);
            const endValue = this.roundFractionToNearestSixteenth(this.currentEndValue);

            rangeSpan.textContent = (
                !startValue.equals(endValue)
                ? `${this.formatValueForDisplay(startValue)} - ${this.formatValueForDisplay(endValue)}`
                : this.formatValueForDisplay(startValue)
            );
        }

        /**
         * @param {Fraction} frac
         * @return {Fraction}
         */
        roundFractionToNearestSixteenth(frac) {
            if (this.totalRange.toDecimal() <= (5/16)) {
                return frac.simplify();
            }
            if (frac.numerator > frac.denominator) {
                if (frac.numerator / frac.denominator % 1 === 0) {
                    return frac.simplify();
                }
                const wholeNum = Math.floor(frac.numerator / frac.denominator);
                const remainderFrac = fraction([frac.numerator - (frac.denominator * wholeNum), frac.denominator]);
                const roundedRemainder = this.roundFractionToNearestSixteenth(remainderFrac);
                return roundedRemainder.plus(wholeNum).simplify();
            } else {
                let closestValue;
                let closestDiff;
                let k = 0;
                while (k <= 16) {
                    const diff = Math.abs(frac.toDecimal() - (k / 16));
                    if (closestDiff === undefined || closestDiff > diff) {
                        closestDiff = diff;
                        closestValue = fraction([k, 16]);
                    }
                    k += 1;
                }
                return closestValue.simplify();
            }
        }

        buildActiveFilter(html = document) {
            const existingActiveFilter = html.querySelector(`.is-plug-slider-filter[data-metaobject-id="${this.dataset.metaobjectId}"]`);
            if (
                !existingActiveFilter
                && this.numericalValues.some(value => value !== undefined)
                && (!this.currentStartValue.equals(this.minValue) || !this.currentEndValue.equals(this.maxValue))
            ) {
                const link = document.getElementById("plug-active-filter-template").content.cloneNode(true).querySelector("a");
                link.dataset.metaobjectId = this.dataset.metaobjectId;
                link.querySelector(".plug-active-filter-label").textContent = `${this.dataset.label}: `;
                this.reformatActiveFilter(link);
                const clearAll = html.querySelector(".active-filters__remove-all");
                if (clearAll) {
                    html.querySelector(".plug-collection__active-filters").insertBefore(link, clearAll);
                } else {
                    html.querySelector(".plug-collection__active-filters").appendChild(link);
                }
            }
        }

        /**
         * @param {Fraction} value
         * @return {string}
         */
        formatValueForDisplay(value) {
            if (this.units === "Inches") {
                return `${value.toString()}"`;
            }
            return value.toString();
        }

        setLineLabelValues() {
            const lineLabels = this.elements.lineLabels;
            const breakpoints = [
                this.minValue,
                this.minValue.plus((this.maxValue.minus(this.minValue)).divideBy(4)),
                this.minValue.plus((this.maxValue.minus(this.minValue)).divideBy(2)),
                this.minValue.plus((this.maxValue.minus(this.minValue)).times(fraction([3, 4]))),
                this.maxValue
            ];
            if (this.minValue.equals(this.maxValue) || this.maxValue.minus(this.minValue).equals(fraction([1, 16]))) {
                breakpoints.forEach((breakpoint, index) => {
                    if (index === 0 || index === breakpoints.length - 1) {
                        lineLabels[index].textContent = this.formatValueForDisplay(this.roundFractionToNearestSixteenth(breakpoint));
                    } else {
                        lineLabels[index].textContent = "";
                    }
                });
            } else {
                breakpoints.forEach((breakpoint, index) => {
                    lineLabels[index].textContent = this.formatValueForDisplay(this.roundFractionToNearestSixteenth(breakpoint));
                });
            }
        }

        computeMinAndMaxValues(values) {
            let minValue = Infinity;
            let maxValue = -Infinity;
            values.forEach(value => {
                if (Array.isArray(value)) {
                    if (value[0].isLessThan(minValue)) {
                        minValue = value[0];
                    }
                    if (value[1].isGreaterThan(maxValue)) {
                        maxValue = value[1];
                    }
                } else if (value && typeof value === "object") {
                    if (value.isLessThan(minValue)) {
                        minValue = value;
                    }
                    if (value.isGreaterThan(maxValue)) {
                        maxValue = value;
                    }
                }
            });
            return [
                minValue,
                maxValue
            ];
        }

        computeValueUnitPerPx() {
            return this.totalRange.divideBy(this.lineWidth);
        }

        initListeners() {
            if (
                this.numericalValues.some(value => value !== undefined)
                && !this.minValue.equals(this.maxValue)
            ) {
                const removeStartControlListeners = this.initStartControl();
                const removeEndControlListeners = this.initEndControl();
                return () => {
                    removeStartControlListeners();
                    removeEndControlListeners();
                };
            } else {
                this.classList.add("is-disabled");
            }
        }

        updateFormInputs(startValue, endValue) {
            startValue = this.roundFractionToNearestSixteenth(startValue);
            endValue = this.roundFractionToNearestSixteenth(endValue);
            // The entire slider range being selected implies that this component is not applying any filters.
            if (startValue.equals(this.minValue) && endValue.equals(this.maxValue)) {
                this.elements.hiddenInputs.forEach(input => {
                    input.checked = false;
                });
                this.elements.hiddenStartInput.checked = false;
                this.elements.hiddenEndInput.checked = false;
                return;
            }

            // Find all filter values in the interval [startValue, endValue].
            const activeValues = [];
            this.numericalValues.forEach((value, index) => {
                if (this.valueIsInRange(value, startValue, endValue)) {
                    activeValues.push(this.relevantFilterValues[index])
                }
            });

            this.elements.hiddenInputs.forEach((input) => {
                const activeValue = activeValues.find(activeValue => (
                    activeValue.paramName === input.name
                    && activeValue.value === input.value
                ));
                input.checked = !!activeValue;
            });
            this.elements.hiddenStartInput.value = startValue;
            this.elements.hiddenEndInput.value = endValue;
            this.elements.hiddenStartInput.checked = true;
            this.elements.hiddenEndInput.checked = true;
        }

        /**
         *
         * @param {Fraction | [Fraction, Fraction] | undefined} value
         * @param {Fraction} start
         * @param {Fraction} end
         */
        valueIsInRange(value, start, end) {
            if (Array.isArray(value)) {
                return (
                    (value[0].isGreaterThanOrEqualTo(start) && value[0].isLessThanOrEqualTo(end))
                    || (value[1].isGreaterThanOrEqualTo(start) && value[1].isLessThanOrEqualTo(end))
                    || (value[0].isLessThanOrEqualTo(start) && value[1].isGreaterThanOrEqualTo(end))
                );
            } else if (value && typeof value === "object") {
                return (
                    value.isGreaterThanOrEqualTo(start)
                    && value.isLessThanOrEqualTo(end)
                );
            }
        }

        initStartControl() {
            const control = this.elements.controlStart;
            let isDragging = false;
            let valueUnitPerPx = this.computeValueUnitPerPx();
            let dragStartValue;

            const handlePointerUp = () => {
                isDragging = false;
                if (!this.currentStartValue.equals(dragStartValue)) {
                    this.emitChange(this.currentStartValue, this.currentEndValue);
                }
            };

            /** @param {PointerEvent} e */
            const handlePointerMove = (e) => {
                // movementX: X coordinate delta since last move event.
                if (!isDragging) return;
                if (e.movementY === 0 && e.movementX === 0) {
                    return;
                }
                const currentValue = this.currentStartValue;
                const valueDelta = valueUnitPerPx.times(e.movementX).fround();
                const newValue = minFraction(this.currentEndValue, maxFraction(this.minValue, currentValue.plus(valueDelta)));
                this.setState(newValue);
            };

            /**
             * @param {PointerEvent} e
             */
            const handlePointerDown = (e) => {
                dragStartValue = this.currentStartValue;
                valueUnitPerPx = this.computeValueUnitPerPx();
                control.setPointerCapture(e.pointerId);
                control.addEventListener("pointerup", handlePointerUp, false);
                control.addEventListener("pointermove", handlePointerMove, false);
                isDragging = true;
            };

            control.addEventListener("pointerdown", handlePointerDown, false);
            control.ondragstart = () => false;

            return () => {
                control.removeEventListener("pointerdown", handlePointerDown, false);
                control.removeEventListener("pointerup", handlePointerUp, false);
                control.removeEventListener("pointermove", handlePointerMove, false);
                control.ondragstart = null;
            };
        }

        initEndControl() {
            const control = this.elements.controlEnd;
            let isDragging = false;
            let valueUnitPerPx = this.computeValueUnitPerPx();
            let dragStartValue;

            const handlePointerUp = () => {
                isDragging = false;
                if (!this.currentEndValue.equals(dragStartValue)) {
                    this.emitChange(this.currentStartValue, this.currentEndValue);
                }
            };

            /** @param {PointerEvent} e */
            const handlePointerMove = (e) => {
                // movementX: X coordinate delta since last move event.
                if (!isDragging) return;
                if (e.movementY === 0 && e.movementX === 0) {
                    return;
                }
                const currentValue = this.currentEndValue;
                const valueDelta = valueUnitPerPx.times(e.movementX).fround();
                const newValue = maxFraction(this.currentStartValue, minFraction(this.maxValue, currentValue.plus(valueDelta)));
                this.setState(undefined, newValue);
            };

            /** @param {PointerEvent} e */
            const handlePointerDown = (e) => {
                dragStartValue = this.currentEndValue;
                valueUnitPerPx = this.computeValueUnitPerPx();
                control.setPointerCapture(e.pointerId);
                control.addEventListener("pointerup", handlePointerUp, false);
                control.addEventListener("pointermove", handlePointerMove, false);
                isDragging = true;
            };

            control.addEventListener("pointerdown", handlePointerDown, false);
            control.ondragstart = () => false;

            return () => {
                control.removeEventListener("pointerdown", handlePointerDown, false);
                control.removeEventListener("pointerup", handlePointerUp, false);
                control.removeEventListener("pointermove", handlePointerMove, false);
                control.ondragstart = null;
            };
        }

        emitChange(startValue, endValue) {
            if (this.quitEarlyObjects.length) {
                this.quitEarlyObjects[this.quitEarlyObjects.length - 1].quitEarly = true;
            }
            this.updateFormInputs(startValue, endValue);
            const parentForm = this.closest("form");
            if (parentForm) {
                this.quitEarlyObjects.push({ quitEarly: false });
                window.PLUG.collectionFilters.handleChange(parentForm, this.quitEarlyObjects[this.quitEarlyObjects.length - 1]);
            } else {
                console.warn("Cannot find parent form.");
            }
        }

        setState(startValue, endValue) {
            let startProgress;
            let endProgress;
            if (startValue !== undefined) {
                this.currentStartValue = startValue;
                if (!this.totalRange.equals(0)) {
                    startProgress = startValue.minus(this.minValue).divideBy(this.totalRange).toDecimal();
                } else {
                    startProgress = 0;
                }
                this.setControlPosition(this.elements.controlStart, startProgress);
            }
            if (endValue !== undefined) {
                this.currentEndValue = endValue;
                if (!this.totalRange.equals(0)) {
                    endProgress = 1 - endValue.minus(this.minValue).divideBy(this.totalRange).toDecimal();
                } else {
                    endProgress = 0;
                }
                this.setControlPosition(this.elements.controlEnd, endProgress);
            }
            if (
                this.currentStartValue
                && this.currentEndValue
                && this.currentStartValue.equals(this.currentEndValue)
                && this.currentEndValue.equals(this.maxValue)
            ) {
                this.classList.add("start-z-2");
            } else {
                this.classList.remove("start-z-2");
            }
            this.elements.slider.setAttribute("aria-valuetext", `from ${this.currentStartValue.toString()} to ${this.currentEndValue.toString()}`);
            this.elements.slider.setAttribute("aria-valuenow", `${this.currentStartValue.toString()},${this.currentEndValue.toString()}`);
            this.setActiveLinePosition(startProgress, endProgress);
        }
    }
    if (!window.customElements.get("plug-fraction-range-slider")) {
        window.customElements.define("plug-fraction-range-slider", PlugFractionRangeSlider);
    }
})();